import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { getGym, updateGym, getCoaches, createCoach, deleteCoach, updateCoachPermissions, changePassword, updateProfile, trackFeatureUsage } from '../services/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/errorUtils';
import axios from 'axios';
import { 
  Building2,
  Users,
  Mail,
  Phone,
  MapPin,
  Plus,
  Trash2,
  Save,
  Eye,
  EyeOff,
  Crown,
  Shield,
  ClipboardList,
  Video,
  Settings2,
  CreditCard,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { cn } from '../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;

export const SettingsPage = () => {
  const { user, gym, isAdmin, refreshGym, refreshUser, isSuperAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [coaches, setCoaches] = useState([]);
  const [gymData, setGymData] = useState({
    name: '',
    email: '',
    address: '',
    phone: ''
  });
  
  // Coach Dialog
  const [coachDialogOpen, setCoachDialogOpen] = useState(false);
  const [newCoach, setNewCoach] = useState({ name: '', email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [savingCoach, setSavingCoach] = useState(false);
  const [deleteCoachId, setDeleteCoachId] = useState(null);
  const [updatingPermissions, setUpdatingPermissions] = useState({});
  
  // Account/Password state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [savingPassword, setSavingPassword] = useState(false);
  const [profileData, setProfileData] = useState({
    name: '',
    email: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Stripe Connect state
  const [stripeStatus, setStripeStatus] = useState(null);
  const [loadingStripe, setLoadingStripe] = useState(false);
  const [connectingStripe, setConnectingStripe] = useState(false);
  
  // Determine default tab from URL params
  const tabParam = searchParams.get('tab');
  const defaultTab = tabParam === 'payments' ? 'payments' : (isSuperAdmin ? 'account' : 'gym');

  const loadData = useCallback(async () => {
    try {
      // SuperAdmin doesn't have a gym, so skip gym fetch for them
      if (user?.role === 'superadmin') {
        setLoading(false);
        return;
      }
      
      const [gymRes, coachesRes] = await Promise.all([
        getGym(),
        isAdmin ? getCoaches() : Promise.resolve({ data: [] })
      ]);
      setGymData({
        name: gymRes.data.name || '',
        email: gymRes.data.email || '',
        address: gymRes.data.address || '',
        phone: gymRes.data.phone || ''
      });
      setCoaches(coachesRes.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [user?.role, isAdmin]);
  
  // Load Stripe Connect status
  const loadStripeStatus = useCallback(async () => {
    if (!isAdmin || isSuperAdmin) return;
    setLoadingStripe(true);
    try {
      const response = await axios.get(`${API}/stripe-connect/status`);
      setStripeStatus(response.data);
      
      // Check if returning from Stripe onboarding
      const success = searchParams.get('success');
      if (success === 'true' && response.data.connected) {
        toast.success('Stripe account connected successfully!');
      }
    } catch (error) {
      console.error('Failed to load Stripe status:', error);
    } finally {
      setLoadingStripe(false);
    }
  }, [isAdmin, isSuperAdmin, searchParams]);
  
  const handleConnectStripe = async () => {
    setConnectingStripe(true);
    try {
      const response = await axios.post(`${API}/stripe-connect/onboard`);
      // Redirect to Stripe onboarding
      window.location.href = response.data.onboarding_url;
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to start Stripe setup'));
      setConnectingStripe(false);
    }
  };
  
  const handleOpenStripeDashboard = async () => {
    try {
      const response = await axios.get(`${API}/stripe-connect/dashboard-link`);
      window.open(response.data.dashboard_url, '_blank');
    } catch (error) {
      toast.error('Failed to open Stripe dashboard');
    }
  };

  useEffect(() => {
    loadData();
    loadStripeStatus();
    trackFeatureUsage('settings', 'view').catch(() => {});
  }, [loadData, loadStripeStatus]);

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        email: user.email || ''
      });
    }
  }, [user]);

  const handleSaveGym = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateGym(gymData);
      await refreshGym();
      toast.success('Gym settings updated');
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCoach = async (e) => {
    e.preventDefault();
    setSavingCoach(true);
    try {
      await createCoach(newCoach);
      toast.success('Coach account created');
      setCoachDialogOpen(false);
      setNewCoach({ name: '', email: '', password: '' });
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create coach'));
    } finally {
      setSavingCoach(false);
    }
  };

  const handleDeleteCoach = async () => {
    if (!deleteCoachId) return;
    try {
      await deleteCoach(deleteCoachId);
      toast.success('Coach removed');
      loadData();
    } catch (error) {
      toast.error('Failed to remove coach');
    } finally {
      setDeleteCoachId(null);
    }
  };

  const handlePermissionToggle = async (coachId, permission, currentValue) => {
    setUpdatingPermissions(prev => ({ ...prev, [`${coachId}-${permission}`]: true }));
    try {
      await updateCoachPermissions(coachId, { [permission]: !currentValue });
      setCoaches(prev => prev.map(coach => 
        coach.id === coachId 
          ? { 
              ...coach, 
              permissions: { 
                ...coach.permissions, 
                [permission]: !currentValue 
              } 
            }
          : coach
      ));
      toast.success('Permission updated');
    } catch (error) {
      toast.error('Failed to update permission');
    } finally {
      setUpdatingPermissions(prev => ({ ...prev, [`${coachId}-${permission}`]: false }));
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setSavingPassword(true);
    try {
      await changePassword({
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });
      toast.success('Password changed successfully');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to change password'));
    } finally {
      setSavingPassword(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await updateProfile({
        name: profileData.name,
        email: profileData.email
      });
      await refreshUser();
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update profile'));
    } finally {
      setSavingProfile(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="settings-page">
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight mb-8">
          Settings
        </h1>

        <Tabs defaultValue={defaultTab} className="space-y-6">
          <TabsList className="bg-zinc-900 border border-zinc-800 p-1">
            {!isSuperAdmin && (
              <TabsTrigger 
                value="gym" 
                className="data-[state=active]:bg-zinc-800 data-[state=active]:text-white"
              >
                <Building2 className="w-4 h-4 mr-2" />
                Gym Profile
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger 
                value="team"
                className="data-[state=active]:bg-zinc-800 data-[state=active]:text-white"
              >
                <Users className="w-4 h-4 mr-2" />
                Team
              </TabsTrigger>
            )}
            {isAdmin && !isSuperAdmin && (
              <TabsTrigger 
                value="payments"
                className="data-[state=active]:bg-zinc-800 data-[state=active]:text-white"
                data-testid="payments-tab"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Payments
              </TabsTrigger>
            )}
            <TabsTrigger 
              value="account"
              className="data-[state=active]:bg-zinc-800 data-[state=active]:text-white"
            >
              <Shield className="w-4 h-4 mr-2" />
              Account
            </TabsTrigger>
          </TabsList>

          {/* Gym Profile Tab */}
          {!isSuperAdmin && (
          <TabsContent value="gym">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-zinc-900/50 border border-zinc-800 p-6 rounded-sm"
            >
              <h2 className="font-heading text-xl font-bold text-white uppercase tracking-tight mb-6">
                Gym Information
              </h2>
              
              <form onSubmit={handleSaveGym} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="gym_name" className="text-zinc-300">Gym Name</Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                      <Input
                        id="gym_name"
                        value={gymData.name}
                        onChange={(e) => setGymData({ ...gymData, name: e.target.value })}
                        className="pl-10 h-12 bg-zinc-900 border-zinc-800 focus:border-red-600"
                        disabled={!isAdmin}
                        data-testid="gym-name-input"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="gym_email" className="text-zinc-300">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                      <Input
                        id="gym_email"
                        type="email"
                        value={gymData.email}
                        onChange={(e) => setGymData({ ...gymData, email: e.target.value })}
                        className="pl-10 h-12 bg-zinc-900 border-zinc-800 focus:border-red-600"
                        disabled={!isAdmin}
                        data-testid="gym-email-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="gym_address" className="text-zinc-300">Address</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                      <Input
                        id="gym_address"
                        value={gymData.address || ''}
                        onChange={(e) => setGymData({ ...gymData, address: e.target.value })}
                        className="pl-10 h-12 bg-zinc-900 border-zinc-800 focus:border-red-600"
                        disabled={!isAdmin}
                        data-testid="gym-address-input"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="gym_phone" className="text-zinc-300">Phone</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                      <Input
                        id="gym_phone"
                        value={gymData.phone || ''}
                        onChange={(e) => setGymData({ ...gymData, phone: e.target.value })}
                        className="pl-10 h-12 bg-zinc-900 border-zinc-800 focus:border-red-600"
                        disabled={!isAdmin}
                        data-testid="gym-phone-input"
                      />
                    </div>
                  </div>
                </div>

                {isAdmin && (
                  <div className="pt-4">
                    <Button
                      type="submit"
                      disabled={saving}
                      className="bg-red-600 hover:bg-red-700 text-white uppercase font-heading font-semibold"
                      data-testid="save-gym-btn"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                )}
              </form>

              {/* Subscription Info */}
              <div className="mt-8 pt-6 border-t border-zinc-800">
                <h3 className="font-heading text-lg font-bold text-white uppercase tracking-tight mb-4">
                  Subscription
                </h3>
                <div className="flex items-center justify-between p-4 bg-zinc-950/50 border border-zinc-800 rounded-sm">
                  <div className="flex items-center gap-3">
                    {gym?.subscription_tier === 'gold' ? (
                      <Crown className="w-6 h-6 text-yellow-500" />
                    ) : (
                      <div className="w-6 h-6 bg-zinc-700 rounded-full" />
                    )}
                    <div>
                      <p className="text-white font-medium capitalize">
                        {gym?.subscription_tier === 'none' ? 'No Subscription' : `${gym?.subscription_tier} Plan`}
                      </p>
                      <p className="text-zinc-500 text-sm capitalize">
                        {gym?.subscription_status || 'inactive'}
                      </p>
                    </div>
                  </div>
                  {isAdmin && (
                    <Button
                      variant="outline"
                      onClick={() => window.location.href = '/subscription'}
                      className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                    >
                      Manage Subscription
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          </TabsContent>
          )}

          {/* Team Tab */}
          {isAdmin && (
            <TabsContent value="team">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-zinc-900/50 border border-zinc-800 p-6 rounded-sm"
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading text-xl font-bold text-white uppercase tracking-tight">
                    Coach Accounts
                  </h2>
                  <Button
                    onClick={() => setCoachDialogOpen(true)}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="add-coach-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Coach
                  </Button>
                </div>

                <p className="text-zinc-400 text-sm mb-6">
                  Coaches have view-only access by default. Enable permissions below to allow editing.
                </p>

                {coaches.length > 0 ? (
                  <div className="space-y-4">
                    {coaches.map((coach) => (
                      <div
                        key={coach.id}
                        className="bg-zinc-950/50 border border-zinc-800 rounded-sm overflow-hidden"
                        data-testid={`coach-card-${coach.id}`}
                      >
                        {/* Coach Info Header */}
                        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-zinc-800 rounded-full flex items-center justify-center">
                              <Users className="w-5 h-5 text-zinc-400" />
                            </div>
                            <div>
                              <p className="text-white font-medium">{coach.name}</p>
                              <p className="text-zinc-500 text-sm">{coach.email}</p>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDeleteCoachId(coach.id)}
                            className="text-zinc-400 hover:text-red-500"
                            data-testid={`delete-coach-${coach.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        {/* Permissions Section */}
                        <div className="p-4 bg-zinc-900/30">
                          <div className="flex items-center gap-2 mb-3">
                            <Settings2 className="w-4 h-4 text-red-500" />
                            <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-400">Permissions</span>
                          </div>
                          
                          <div className="space-y-3">
                            {/* Edit Assessments Permission */}
                            <div className="flex items-center justify-between p-3 bg-zinc-950/50 rounded-sm">
                              <div className="flex items-center gap-3">
                                <ClipboardList className="w-4 h-4 text-zinc-500" />
                                <div>
                                  <p className="text-white text-sm">Edit Assessments</p>
                                  <p className="text-zinc-500 text-xs">Can create and modify fighter assessments</p>
                                </div>
                              </div>
                              <Switch
                                checked={coach.permissions?.can_edit_assessments || false}
                                onCheckedChange={() => handlePermissionToggle(
                                  coach.id, 
                                  'can_edit_assessments', 
                                  coach.permissions?.can_edit_assessments || false
                                )}
                                disabled={updatingPermissions[`${coach.id}-can_edit_assessments`]}
                                data-testid={`toggle-assessments-${coach.id}`}
                              />
                            </div>
                            
                            {/* Edit Video Notes Permission */}
                            <div className="flex items-center justify-between p-3 bg-zinc-950/50 rounded-sm">
                              <div className="flex items-center gap-3">
                                <Video className="w-4 h-4 text-zinc-500" />
                                <div>
                                  <p className="text-white text-sm">Edit Video Notes</p>
                                  <p className="text-zinc-500 text-xs">Can add and edit notes on training videos</p>
                                </div>
                              </div>
                              <Switch
                                checked={coach.permissions?.can_edit_video_notes || false}
                                onCheckedChange={() => handlePermissionToggle(
                                  coach.id, 
                                  'can_edit_video_notes', 
                                  coach.permissions?.can_edit_video_notes || false
                                )}
                                disabled={updatingPermissions[`${coach.id}-can_edit_video_notes`]}
                                data-testid={`toggle-videos-${coach.id}`}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-zinc-500">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No coaches added yet</p>
                  </div>
                )}
              </motion.div>
            </TabsContent>
          )}

          {/* Payments Tab - Stripe Connect */}
          {isAdmin && !isSuperAdmin && (
            <TabsContent value="payments">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-zinc-900/50 border border-zinc-800 p-6 rounded-sm"
              >
                <h2 className="font-heading text-xl font-bold text-white uppercase tracking-tight mb-6">
                  Payment Settings
                </h2>
                
                <div className="space-y-6">
                  {/* Stripe Connect Status */}
                  <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-violet-600 to-indigo-600 rounded-sm flex items-center justify-center">
                          <CreditCard className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="font-heading text-lg font-bold text-white">Stripe Connect</h3>
                          <p className="text-zinc-400 text-sm">Accept payments from your members</p>
                        </div>
                      </div>
                      
                      {loadingStripe ? (
                        <div className="flex items-center gap-2 text-zinc-400">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span className="text-sm">Checking status...</span>
                        </div>
                      ) : stripeStatus?.connected ? (
                        <div className="flex items-center gap-2">
                          {stripeStatus.status === 'active' ? (
                            <span className="flex items-center gap-1.5 text-green-500 text-sm">
                              <CheckCircle className="w-4 h-4" />
                              Connected
                            </span>
                          ) : stripeStatus.status === 'pending_verification' ? (
                            <span className="flex items-center gap-1.5 text-yellow-500 text-sm">
                              <AlertCircle className="w-4 h-4" />
                              Pending Verification
                            </span>
                          ) : (
                            <span className="flex items-center gap-1.5 text-yellow-500 text-sm">
                              <AlertCircle className="w-4 h-4" />
                              Setup Incomplete
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-zinc-500 text-sm">Not Connected</span>
                      )}
                    </div>
                    
                    <div className="mt-6 pt-6 border-t border-zinc-800">
                      {!stripeStatus?.connected ? (
                        <div className="space-y-4">
                          <p className="text-zinc-400 text-sm">
                            Connect your Stripe account to start accepting membership payments from your gym members. 
                            Funds will be deposited directly into your bank account.
                          </p>
                          <ul className="text-zinc-500 text-sm space-y-2">
                            <li className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              Accept credit/debit card payments
                            </li>
                            <li className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              Automatic recurring billing
                            </li>
                            <li className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              Direct deposit to your bank
                            </li>
                          </ul>
                          <Button
                            onClick={handleConnectStripe}
                            disabled={connectingStripe}
                            className="bg-violet-600 hover:bg-violet-700 text-white mt-4"
                            data-testid="connect-stripe-btn"
                          >
                            {connectingStripe ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Connecting...
                              </>
                            ) : (
                              <>
                                <CreditCard className="w-4 h-4 mr-2" />
                                Connect with Stripe
                              </>
                            )}
                          </Button>
                        </div>
                      ) : stripeStatus.status === 'active' ? (
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="bg-zinc-900 p-4 rounded-sm">
                              <p className="text-zinc-500 text-xs uppercase mb-1">Card Payments</p>
                              <p className="text-green-500 font-medium">Enabled</p>
                            </div>
                            <div className="bg-zinc-900 p-4 rounded-sm">
                              <p className="text-zinc-500 text-xs uppercase mb-1">Payouts</p>
                              <p className="text-green-500 font-medium">Enabled</p>
                            </div>
                          </div>
                          <p className="text-zinc-400 text-sm">
                            Your account is fully set up. You can now create membership plans and accept payments from members.
                          </p>
                          <Button
                            onClick={handleOpenStripeDashboard}
                            variant="outline"
                            className="border-zinc-700"
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Open Stripe Dashboard
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <p className="text-yellow-500 text-sm">
                            {stripeStatus.message}
                          </p>
                          <Button
                            onClick={handleConnectStripe}
                            disabled={connectingStripe}
                            className="bg-yellow-600 hover:bg-yellow-700 text-white"
                          >
                            {connectingStripe ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Loading...
                              </>
                            ) : (
                              <>
                                Complete Setup
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Info box */}
                  <div className="bg-blue-600/10 border border-blue-600/30 rounded-sm p-4">
                    <p className="text-blue-400 text-sm">
                      <strong>Note:</strong> After connecting Stripe, you can create membership plans in the 
                      <span className="font-medium"> Membership Plans</span> section to start accepting recurring payments.
                    </p>
                  </div>
                </div>
              </motion.div>
            </TabsContent>
          )}

          {/* Account Tab */}
          <TabsContent value="account">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {/* Profile Information Section */}
              <div className="bg-zinc-900/50 border border-zinc-800 p-6 rounded-sm">
                <h2 className="font-heading text-xl font-bold text-white uppercase tracking-tight mb-6">
                  Profile Information
                </h2>
                
                <form onSubmit={handleUpdateProfile} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="profile_name" className="text-zinc-300">Name</Label>
                      <Input
                        id="profile_name"
                        value={profileData.name}
                        onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                        className="h-12 bg-zinc-900 border-zinc-800 focus:border-red-600"
                        placeholder="Your name"
                        data-testid="profile-name-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="profile_email" className="text-zinc-300">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                        <Input
                          id="profile_email"
                          type="email"
                          value={profileData.email}
                          onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                          className="pl-10 h-12 bg-zinc-900 border-zinc-800 focus:border-red-600"
                          placeholder="your@email.com"
                          data-testid="profile-email-input"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between pt-2">
                    <div className="flex items-center gap-2">
                      <span className={cn(
                        "px-3 py-1 rounded-sm text-xs uppercase font-medium",
                        user?.role === 'superadmin' ? "bg-yellow-600/20 text-yellow-500" :
                        user?.role === 'admin' ? "bg-red-600/20 text-red-500" : "bg-zinc-700/20 text-zinc-400"
                      )}>
                        {user?.role}
                      </span>
                    </div>
                    <Button
                      type="submit"
                      disabled={savingProfile}
                      className="bg-red-600 hover:bg-red-700 text-white uppercase font-heading font-semibold"
                      data-testid="save-profile-btn"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {savingProfile ? 'Saving...' : 'Update Profile'}
                    </Button>
                  </div>
                </form>
              </div>

              {/* Change Password Section */}
              <div className="bg-zinc-900/50 border border-zinc-800 p-6 rounded-sm">
                <h2 className="font-heading text-xl font-bold text-white uppercase tracking-tight mb-6">
                  Change Password
                </h2>
                
                <form onSubmit={handleChangePassword} className="space-y-6">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="current_password" className="text-zinc-300">Current Password</Label>
                      <div className="relative">
                        <Input
                          id="current_password"
                          type={showPasswords.current ? 'text' : 'password'}
                          value={passwordData.currentPassword}
                          onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                          className="h-12 bg-zinc-900 border-zinc-800 focus:border-red-600 pr-10"
                          placeholder="••••••••"
                          required
                          data-testid="current-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                        >
                          {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="new_password" className="text-zinc-300">New Password</Label>
                        <div className="relative">
                          <Input
                            id="new_password"
                            type={showPasswords.new ? 'text' : 'password'}
                            value={passwordData.newPassword}
                            onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                            className="h-12 bg-zinc-900 border-zinc-800 focus:border-red-600 pr-10"
                            placeholder="••••••••"
                            required
                            minLength={8}
                            data-testid="new-password-input"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                          >
                            {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="confirm_password" className="text-zinc-300">Confirm New Password</Label>
                        <div className="relative">
                          <Input
                            id="confirm_password"
                            type={showPasswords.confirm ? 'text' : 'password'}
                            value={passwordData.confirmPassword}
                            onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                            className="h-12 bg-zinc-900 border-zinc-800 focus:border-red-600 pr-10"
                            placeholder="••••••••"
                            required
                            minLength={8}
                            data-testid="confirm-password-input"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                          >
                            {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-zinc-500 text-sm">
                    Password must be at least 8 characters long.
                  </p>
                  
                  <div className="pt-2">
                    <Button
                      type="submit"
                      disabled={savingPassword}
                      className="bg-red-600 hover:bg-red-700 text-white uppercase font-heading font-semibold"
                      data-testid="change-password-btn"
                    >
                      <Shield className="w-4 h-4 mr-2" />
                      {savingPassword ? 'Changing...' : 'Change Password'}
                    </Button>
                  </div>
                </form>
              </div>
            </motion.div>
          </TabsContent>
        </Tabs>

        {/* Add Coach Dialog */}
        <Dialog open={coachDialogOpen} onOpenChange={setCoachDialogOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-white font-heading uppercase">Add Coach Account</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateCoach} className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Name</Label>
                <Input
                  value={newCoach.name}
                  onChange={(e) => setNewCoach({ ...newCoach, name: e.target.value })}
                  placeholder="Coach Name"
                  className="h-12 bg-zinc-950 border-zinc-800 focus:border-red-600"
                  required
                  data-testid="new-coach-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Email</Label>
                <Input
                  type="email"
                  value={newCoach.email}
                  onChange={(e) => setNewCoach({ ...newCoach, email: e.target.value })}
                  placeholder="coach@gym.com"
                  className="h-12 bg-zinc-950 border-zinc-800 focus:border-red-600"
                  required
                  data-testid="new-coach-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Password</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={newCoach.password}
                    onChange={(e) => setNewCoach({ ...newCoach, password: e.target.value })}
                    placeholder="••••••••"
                    className="h-12 bg-zinc-950 border-zinc-800 focus:border-red-600 pr-10"
                    required
                    minLength={6}
                    data-testid="new-coach-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <DialogFooter className="pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCoachDialogOpen(false)}
                  className="border-zinc-700"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={savingCoach}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="save-coach-btn"
                >
                  {savingCoach ? 'Creating...' : 'Create Coach'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Coach Confirmation */}
        <AlertDialog open={!!deleteCoachId} onOpenChange={() => setDeleteCoachId(null)}>
          <AlertDialogContent className="bg-zinc-900 border-zinc-800">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Remove Coach</AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                Are you sure you want to remove this coach? They will no longer be able to access the system.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteCoach}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default SettingsPage;
